/*
 * aura-tryon.js: <aura-tryon>, the AI try-on in the product page's Your car tab (spec 09, snippets/tryon.liquid).
 * The Color Explorer's try-on (Explorer4 sections/overlays/TryOn), run in place in the media column:
 *   choose   pick or drop a photo (reuse: the session's photo, one click to paint this color)
 *   working  the photo, large, with the 12° scan line laying the film's colors over it (base, then shift colors,
 *            from the explorer's catalog) as a `color` blend and a bar filling in the same colors, while the
 *            explorer's /ai-gen-nb repaints it
 *   done     the result fills the stage: Download, Share (where the browser shares files), hold to see the
 *            original, try another photo
 * HEIC/HEIF photos (iPhone) open natively where the browser can (Safari); elsewhere assets/heic-to.js (heic-to 1.6.6,
 * libheif 1.23.6 in a worker, LGPL-3.0, unmodified; about 3 MB) loads on first need and converts them to JPEG.
 * The photo is sized to 1024 px at most and sent as JPEG with the product title (the explorer's color name;
 * the server builds the prompt and the swatch from it). The result keeps its own shape and gets the Aura
 * watermark bottom left. Leaving the tab doesn't cancel a run: the result waits in the panel and a toast
 * offers to show it. Each run is stamped; a late or superseded response is dropped.
 * Opened by aura.openFeature('tryon') (aura.js), which switches to the Your car tab, and by the tab (aura-product.js).
 * Sign-up gate: a visitor we have nothing for (not signed in, no email or phone given here, in the footer or the chat,
 * no working code typed or followed from a link, no unlocked discount) gets data-free renders (Theme settings >
 * Discount sign-up, 2 by default; counted on the device in aura.tryon.runs). The next run stops in the `locked`
 * state: the photo stays, with the gate's pitch over it, anchored to the top, and an email form and "Have a code?"
 * right in the panel (through aura-meerkat.js signUpEmail and applyCode, no modal). An email or a working code
 * runs the held photo at once; a reload under it (aura.tryon.resume) starts it again. Off without the sign-up on
 * the page (shop accounts, the sign-up off), since there would be nowhere to sign up.
 */
(() => {
  const aura = () => window.aura;
  const track = (n, p) => aura() && aura().track(n, p);
  const PHOTO_KEY = 'aura.tryon.photo';
  const RUNS_KEY = 'aura.tryon.runs';     // finished renders on this device (localStorage)
  const RESUME_KEY = 'aura.tryon.resume'; // the handle to run once the sign-up's reload lands (sessionStorage)
  const ls = {
    get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };
  /* A Meerkat lead: an email or phone given (the sign-up modal, the footer, the chat), or the Marketing API's id
     for one. An `mkt` equal to the anonymous id came back on an explorer link, so it doesn't count */
  function hasLead() {
    if (ls.get('mkt_email') || ls.get('mkt_phone') || ls.get('lmkt')) return true;
    const mkt = ls.get('mkt');
    return !!mkt && mkt !== ls.get('amkt');
  }
  const fillIn = (str, vars) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (vars[k] != null ? vars[k] : m));
  const TIMEOUT = 90000;     // the explorer's 60 s plus room for its second attempt when a render comes back reframed
  const MAX_SIDE = 1024;     // longest side of the photo sent
  const MARK = 128 / 1024;   // watermark size and inset, as a share of the result's short side (Explorer4: 128 and 20 on 1024)
  const MARK_INSET = 20 / 1024;
  const CHOOSE_GUARD = 1000; // repeat clicks on "Choose photo" within 1 s are ignored
  const PAINT_PASS = 1600;   // one pass of the scan line
  const PAINT_CYCLE = 2000;  // the pass plus a hold on the laid color
  const PAINT_EASE = 12500;  // the bar fills as 1 - e^(-t / this): about 90% at 30 s
  const PAINT_MAX = 0.97;
  const SLANT = Math.tan(12 * Math.PI / 180); // --squeegee-angle

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const touch = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /* Shopify's t filter HTML-escapes ("didn&#39;t"); these strings go in as text */
  function decodeAll(obj) {
    const t = document.createElement('textarea');
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => { t.innerHTML = v; return [k, t.value]; }));
  }

  function hexOf(value) {
    let h = String(value || '').trim().replace('#', '').toLowerCase();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return h.length === 6 && !/[^0-9a-f]/.test(h) ? `#${h}` : '';
  }

  /* The color as a glow on the dark screen: no darker than 45% lightness, so black and deep films still show */
  function accent(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16 & 255) / 255; const g = (n >> 8 & 255) / 255; const b = (n & 255) / 255;
    const max = Math.max(r, g, b); const min = Math.min(r, g, b); const l = (max + min) / 2; const d = max - min;
    let h = 0; let s = 0;
    if (d) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    }
    return `hsl(${Math.round(h * 60)} ${Math.round(s * 100)}% ${Math.round(Math.max(l, 0.45) * 100)}%)`;
  }

  /* 'Gloss Paint Metallic Viper Green' -> 'gloss-paint-metallic-viper-green', the product handle */
  const perma = (name) => String(name || '').trim().split(' ').join('-').toLowerCase();

  /* Explorer4 TryOn paletteFor: shift films blend in one or two more colors; an opal (5 shifts) is mostly its third */
  function paletteFor(m, fallback) {
    const base = hexOf(m.color_base || m.uPaintColor) || fallback;
    const shifts = Number(m.shifts) || 0;
    const shifting = shifts > 0.5 && (!(Number(m.iridescent) >= 0.5) || shifts > 4);
    let list = [base];
    if (shifting) list.push(hexOf(m.color_shift1 || m.uShiftColor));
    if (shifting && shifts > 1.5) list.push(hexOf(m.color_shift2));
    if (shifts > 4) list = [list[2], list[0], list[1]];
    return list.filter((hex, i) => hex && list.indexOf(hex) === i);
  }

  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => (img.naturalWidth ? resolve(img) : reject(new Error('empty image')));
      img.onerror = () => reject(new Error('decode'));
      img.src = src;
    });
  }

  /* HEIC/HEIF by its ftyp brand (bytes 8-12), the check heic-to's isHeic makes, without loading the library */
  const HEIF_BRANDS = ['mif1', 'msf1', 'heic', 'heix', 'hevc', 'hevx'];
  async function isHeif(file) {
    if (/\.hei[cf]$/i.test(file.name || '') || /^image\/hei[cf]/i.test(file.type || '')) return true;
    const head = new Uint8Array(await file.slice(4, 12).arrayBuffer());
    const ascii = String.fromCharCode(...head);
    return ascii.slice(0, 4) === 'ftyp' && HEIF_BRANDS.includes(ascii.slice(4).replace('\0', ' ').trim());
  }

  let heicLib = null;
  function loadHeic(src) {
    if (!heicLib) {
      heicLib = new Promise((resolve, reject) => {
        if (window.HeicTo) return resolve(window.HeicTo);
        const s = document.createElement('script');
        s.src = src;
        s.onload = () => (window.HeicTo ? resolve(window.HeicTo) : reject(new Error('heic-to')));
        s.onerror = () => reject(new Error('heic-to'));
        document.head.append(s);
      });
      heicLib.catch(() => { heicLib = null; });
    }
    return heicLib;
  }

  /* The browser's own decoder first; a HEIC it can't open goes through heic-to as a JPEG */
  async function decodePhoto(file, heicSrc, onConvert) {
    const url = URL.createObjectURL(file);
    try {
      return await loadImage(url);
    } catch (e) {
      if (!heicSrc || !(await isHeif(file))) throw e;
    } finally {
      URL.revokeObjectURL(url);
    }
    if (onConvert) onConvert();
    const heicTo = await loadHeic(heicSrc);
    const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
    const jpegUrl = URL.createObjectURL(jpeg);
    try { return await loadImage(jpegUrl); } finally { URL.revokeObjectURL(jpegUrl); }
  }

  /* Longest side at most 1024 px, aspect kept, JPEG at 0.9. Some browsers give a .heic an empty or generic type,
     so the name lets it through */
  async function encodePhoto(file, heicSrc, onConvert) {
    if (file.type && !file.type.startsWith('image/') && !/\.hei[cf]$/i.test(file.name || '')) throw new Error('not an image');
    const img = await decodePhoto(file, heicSrc, onConvert);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.9);
  }

  class AuraTryon extends HTMLElement {
    connectedCallback() {
      const $ = (s) => this.querySelector(s);
      this.root = $('.tryon');
      this.strings = decodeAll(JSON.parse($('[data-tryon-strings]').textContent));
      this.drop = $('[data-tryon-drop]');
      this.fitBox = $('[data-tryon-fit]');
      this.photo = $('[data-tryon-photo]');
      this.laid = $('[data-tryon-laid]');
      this.wipe = $('[data-tryon-wipe]');
      this.edge = $('[data-tryon-edge]');
      this.barFill = $('[data-tryon-barf]');
      this.file = $('[data-tryon-file]');
      this.statusEl = $('[data-tryon-status]');
      this.err = $('[data-tryon-err]');
      this.resultImg = $('[data-tryon-result-img]');
      this.resultFit = $('[data-tryon-result-fit]');
      this.stage = $('.tryon__stage');
      this.original = $('[data-tryon-original]');
      this.download = $('[data-tryon-download]');
      this.shareBtn = $('[data-tryon-share]');
      this.compareBtn = $('[data-tryon-compare]');
      this.hint = $('[data-tryon-drop-hint]');
      this.gateBody = $('[data-tryon-gate-body]');
      this.panel = this.closest('[role="tabpanel"]');

      this.palette = [hexOf(this.dataset.hex) || '#808080'];
      this.state = 'choose';
      this.stamp = 0;
      this.result = null;      // {url, blob} of the last result for this color
      this.lastChoose = 0;
      try { this.sessionPhoto = sessionStorage.getItem(PHOTO_KEY) || ''; } catch (e) { this.sessionPhoto = ''; }
      if (touch() && this.hint.dataset.touch) this.hint.textContent = this.hint.dataset.touch;

      $('[data-tryon-choose]').addEventListener('click', () => this.chooseFile());
      $('[data-tryon-another]').addEventListener('click', () => this.chooseFile());
      $('[data-tryon-retry]').addEventListener('click', () => this.chooseFile());
      $('[data-tryon-again]').addEventListener('click', () => { if (this.sessionPhoto) this.generate(); });
      this.file.addEventListener('change', () => {
        const f = this.file.files && this.file.files[0];
        this.file.value = '';
        if (f) this.takeFile(f);
      });
      this.shareBtn.addEventListener('click', () => this.share());
      this.download.addEventListener('click', () => {
        // A button, not a link with no href (Lighthouse crawlable-anchors): save the render through a temporary link
        if (!this.result) return;
        const a = document.createElement('a');
        a.href = this.result.url;
        a.download = this.download.dataset.filename || 'aura-on-my-car.jpg';
        document.body.append(a);
        a.click();
        a.remove();
        track('aura_tryon_saved', { handle: this.dataset.handle, how: 'download' });
      });
      this.wireCompare();
      this.wireDrop();

      this.ro = new ResizeObserver(() => this.fit());
      this.ro.observe(this.stage);
      this.resultImg.addEventListener('load', () => this.fit());

      /* The gate: its email and code forms sign up in place (aura-meerkat.js); an email, phone, code or unlocked
         discount from anywhere else (the modal, the footer, the chat) lets the held run go as well */
      $('[data-tryon-gate-form]').addEventListener('submit', (e) => this.submitEmail(e));
      $('[data-tryon-code-form]').addEventListener('submit', (e) => this.submitCode(e));
      this.onLead = (e) => this.leadGiven((e && e.detail) || {});
      document.addEventListener('aura:lead', this.onLead);
      document.addEventListener('aura:unlock', this.onLead);

      this.render();
      this.resume();
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      this.paint(false);
      clearTimeout(this.runTimer);
      document.removeEventListener('aura:lead', this.onLead);
      document.removeEventListener('aura:unlock', this.onLead);
    }

    /* ---------- Open ---------- */

    shown() {
      return !this.panel || !this.panel.hidden;
    }

    /* Shows the Your car tab (from the buy box, a link or the toast), scrolled into view on phones. The tab
       itself keeps focus when it's the opener; from elsewhere, focus goes to the state's first action */
    open(opener, surface) {
      this.loadPalette();
      const held = this.state === 'locked' && this.gated();
      if (this.state !== 'working' && this.state !== 'done' && !held) this.state = this.sessionPhoto ? 'reuse' : 'choose';
      this.render();
      const tabs = this.closest('aura-media-tabs');
      const tab = tabs && tabs.querySelector('[data-tab="your_car"]');
      if (tab && tab.getAttribute('aria-selected') !== 'true' && tabs.select) tabs.select(tab);
      track('aura_tryon_opened', { surface: surface || 'pdp', built: true, has_photo: !!this.sessionPhoto });
      if (opener !== tab) {
        const top = (tabs || this).getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.5) (tabs || this).scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
        const focus = { reuse: '[data-tryon-again]', choose: '[data-tryon-choose]', done: '[data-tryon-download]', locked: '[data-tryon-gate-form] input' }[this.state];
        if (focus) this.querySelector(focus).focus({ preventScroll: true });
      }
      requestAnimationFrame(() => this.fit());
      return { ok: true };
    }

    /* ---------- State ---------- */

    setState(next) {
      this.state = next;
      this.render();
    }

    render() {
      const s = this.state;
      this.root.dataset.state = s;
      this.root.setAttribute('aria-busy', String(s === 'working'));
      this.statusEl.textContent = s === 'working' ? this.strings.working : s === 'done' ? this.strings.done : '';
      if ((s === 'reuse' || s === 'working' || s === 'locked') && this.sessionPhoto && this.photo.getAttribute('src') !== this.sessionPhoto) {
        this.photo.src = this.sessionPhoto;
        this.photo.decode?.().then(() => this.fit()).catch(() => {});
      }
      this.paint(s === 'working');
      this.fit();
    }

    /* The photo's and the result's boxes take the image's shape, as large as the stage allows, so the coat covers
       only the photo and the result's tag sits on its corner */
    fit() {
      [[this.fitBox, this.photo], [this.resultFit, this.resultImg]].forEach(([box, img]) => {
        const w = img.naturalWidth; const h = img.naturalHeight;
        const W = this.stage.clientWidth; const H = this.stage.clientHeight;
        if (!w || !h || !W || !H) return;
        const scale = Math.min(W / w, H / h);
        box.style.width = `${Math.round(w * scale)}px`;
        box.style.height = `${Math.round(h * scale)}px`;
      });
    }

    /* ---------- Picking a photo ---------- */

    chooseFile() {
      const now = Date.now();
      if (now - this.lastChoose < CHOOSE_GUARD || this.state === 'working') return;
      this.lastChoose = now;
      this.file.click();
    }

    wireDrop() {
      const hasFiles = (e) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
      const over = (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        const ok = this.state !== 'working';
        e.dataTransfer.dropEffect = ok ? 'copy' : 'none';
        this.drop.classList.toggle('is-over', ok);
      };
      this.root.addEventListener('dragenter', over);
      this.root.addEventListener('dragover', over);
      this.root.addEventListener('dragleave', (e) => {
        if (!e.relatedTarget || !this.root.contains(e.relatedTarget)) this.drop.classList.remove('is-over');
      });
      this.root.addEventListener('drop', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        this.drop.classList.remove('is-over');
        if (this.state === 'working') return;
        const files = [...e.dataTransfer.files];
        const f = files.find((x) => (x.type || '').startsWith('image/')) || files[0];
        if (f) this.takeFile(f);
      });
    }

    async takeFile(f) {
      if (this.state === 'working' || this.reading) return;
      this.err.textContent = '';
      this.reading = true;
      /* Converting a HEIC takes a second or two (more the first time, while the decoder loads): say so */
      const reading = () => { this.statusEl.textContent = this.strings.reading; this.root.setAttribute('aria-busy', 'true'); };
      try {
        this.sessionPhoto = await encodePhoto(f, this.dataset.heic, reading);
      } catch (e) {
        this.fail(this.strings.failed, 'bad_photo');
        return;
      } finally {
        this.reading = false;
        this.render();
      }
      try { sessionStorage.setItem(PHOTO_KEY, this.sessionPhoto); } catch (e) { /* too big for storage: kept for this page only */ }
      this.generate();
    }

    /* ---------- The sign-up gate ---------- */

    /* The sign-up (aura-meerkat.js), when it is on the page to sign up with (not for shop accounts or with the
       sign-up off) */
    meerkat() {
      const el = document.querySelector('aura-meerkat');
      return el && el.canUnlock && el.signUpEmail ? el : null;
    }

    freeRuns() {
      const n = parseInt(this.dataset.free, 10);
      return Number.isFinite(n) && n >= 0 ? n : null;
    }

    runs() { return parseInt(ls.get(RUNS_KEY), 10) || 0; }

    /* Through the gate for good: signed in, a lead (email or phone), a working code typed or followed from a link,
       or the discount unlocked on this device or cart */
    passed() {
      if (this.dataset.signedIn === 'true' || hasLead() || ls.get('mkt_code')) return true;
      if (ls.get('aura.discount.v1') || ls.get('aura.unlock.v1') || document.documentElement.classList.contains('is-unlocked')) return true;
      const m = this.meerkat();
      return !!(m && m.unlocked);
    }

    /* Out of free renders with nothing to show for it */
    gated() {
      const free = this.freeRuns();
      if (free === null || this.passed()) return false;
      return this.runs() >= free && !!this.meerkat();
    }

    /* The run stops here: the photo stays under the gate's pitch and its email form */
    lock() {
      this.comparing(false);
      this.setState('locked');
      this.paintGate();
      track('aura_tryon_gated', { handle: this.dataset.handle, runs: this.runs() });
      if (this.shown() && !touch()) this.querySelector('[data-tryon-gate-form] input').focus({ preventScroll: true });
    }

    /* "You'll also get $50 off a full roll" (or the small-item percent on a cheap variant), from the sign-up's offer */
    async paintGate() {
      const m = this.meerkat();
      if (!m || !this.gateBody) return;
      await m.loaded;
      const o = m.offer();
      const offer = fillIn(o.small ? this.strings.gate_offer_small : this.strings.gate_offer_full, { amount: o.amount, under: m.cfg.under_label });
      this.gateBody.textContent = fillIn(this.strings.gate_body, { offer });
    }

    /* The gate's email: onto the Meerkat list with the discount unlocked, then the held render runs (leadGiven) */
    submitEmail(e) {
      e.preventDefault();
      const input = e.currentTarget.querySelector('input');
      const error = e.currentTarget.querySelector('.field__error');
      const m = this.meerkat();
      const r = m ? m.signUpEmail(input.value, 'tryon') : { ok: false };
      input.toggleAttribute('aria-invalid', !r.ok);
      error.hidden = r.ok;
      if (!r.ok) { input.focus(); return; }
      // aura:lead normally gets here first; this covers a sign-up that fired no event
      this.leadGiven({ field: 'email', surface: 'tryon' });
    }

    /* The gate's "Have a code?": checked like the modal's (aura-meerkat.js applyCode). A working code lets the run go */
    async submitCode(e) {
      e.preventDefault();
      const form = e.currentTarget;
      const input = form.querySelector('input');
      const button = form.querySelector('button');
      const msg = form.querySelector('[data-tryon-code-msg]');
      const say = (text, tone) => {
        msg.textContent = text || '';
        msg.hidden = !text;
        msg.classList.toggle('is-ok', tone === 'ok');
        msg.classList.toggle('is-error', tone === 'error');
      };
      const m = this.meerkat();
      if (!m || button.disabled) return;
      button.disabled = true;
      say(this.strings.code_checking);
      let r;
      try { r = await m.applyCode(input.value, 'tryon'); } catch (err) { r = { ok: false, retry: true, text: '' }; }
      button.disabled = false;
      if (!r.ok) {
        if (!r.retry) input.setAttribute('aria-invalid', 'true');
        say(r.text, 'error');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      say(r.text, 'ok');
      this.leadGiven({ field: 'code', surface: 'tryon' });
    }

    /* An email, phone, code or unlock just now (this gate, or the modal, footer or chat): the held render runs at
       once. If the page reloads under it (the footer reloads into the new prices), it starts again after (resume) */
    leadGiven(detail) {
      if (this.state !== 'locked' || this.gated()) return;
      track('aura_tryon_unlocked', { handle: this.dataset.handle, field: detail.field, surface: detail.surface });
      if (!this.sessionPhoto) { this.setState('choose'); return; }
      try { sessionStorage.setItem(RESUME_KEY, this.dataset.handle); } catch (e) { /* the photo is kept: one click */ }
      this.setState('reuse');
      this.generate();
    }

    /* Back from a reload mid-run: open the Your car tab and run the held photo */
    resume() {
      let handle = null;
      try { handle = sessionStorage.getItem(RESUME_KEY); sessionStorage.removeItem(RESUME_KEY); } catch (e) { /* private mode */ }
      if (handle !== this.dataset.handle || !this.sessionPhoto) return;
      const tabs = this.closest('aura-media-tabs');
      (tabs ? customElements.whenDefined('aura-media-tabs') : Promise.resolve()).then(() => requestAnimationFrame(() => {
        this.open(null, 'signup');
        this.generate();
      }));
    }

    /* ---------- The run ---------- */

    async generate() {
      if (!this.sessionPhoto || this.state === 'working') return;
      if (this.gated()) { this.lock(); return; }
      const myStamp = this.stamp = Math.max(Date.now(), this.stamp + 1);
      const t0 = performance.now();
      this.err.textContent = '';
      this.comparing(false);
      this.setState('working');
      track('aura_tryon_started', { handle: this.dataset.handle });
      if (aura() && aura().taste) aura().taste.add('tryon', this.dataset.handle);

      const ctrl = new AbortController();
      let timedOut = false;
      clearTimeout(this.runTimer);
      this.runTimer = setTimeout(() => { timedOut = true; ctrl.abort(); }, TIMEOUT);

      let result;
      try {
        const res = await fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ color: this.dataset.color, inputImage: this.sessionPhoto }),
          signal: ctrl.signal
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        const b64 = data && typeof data.base64 === 'string' ? data.base64 : '';
        if (!b64) throw new Error('no image');
        if (myStamp !== this.stamp) return;
        const img = await loadImage(b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`);
        result = await this.compose(img);
      } catch (e) {
        if (myStamp !== this.stamp) return;
        this.fail(timedOut ? this.strings.timeout : this.strings.failed, timedOut ? 'timeout' : 'failed');
        return;
      } finally {
        clearTimeout(this.runTimer);
      }
      if (myStamp !== this.stamp) return;

      if (this.result) URL.revokeObjectURL(this.result.url);
      this.result = result;
      this.resultImg.src = result.url;
      this.original.src = this.sessionPhoto;
      this.shareBtn.hidden = !this.canShare();
      ls.set(RUNS_KEY, String(this.runs() + 1));
      this.clearResume();
      const hadFocus = this.root.contains(document.activeElement) || document.activeElement === document.body;
      this.setState('done');
      track('aura_tryon_done', { handle: this.dataset.handle, ms: Math.round(performance.now() - t0) });
      document.dispatchEvent(new CustomEvent('aura:tryon-result', { detail: { handle: this.dataset.handle, url: result.url } }));
      if (!this.shown()) { if (aura()) aura().toast(this.strings.ready, { actionLabel: this.strings.view, onAction: () => this.open(null, 'toast') }); }
      else if (hadFocus) this.download.focus({ preventScroll: true });
    }

    clearResume() {
      try { sessionStorage.removeItem(RESUME_KEY); } catch (e) { /* private mode */ }
    }

    fail(text, reason) {
      this.clearResume();
      clearTimeout(this.runTimer);
      this.err.textContent = text;
      this.setState(this.result ? 'done' : this.sessionPhoto ? 'reuse' : 'choose');
      if (this.state === 'done') this.err.textContent = '';
      if (aura() && !this.shown()) aura().toast(text);
      track('aura_tryon_failed', { handle: this.dataset.handle, reason });
    }

    /* The result at its own size and shape (capped at 2048 px), the watermark bottom left */
    async compose(img) {
      const scale = Math.min(1, 2048 / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.round(img.naturalWidth * scale); const h = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, w, h);
      try {
        const mark = await loadImage(this.dataset.watermark);
        const short = Math.min(w, h);
        const size = Math.round(short * MARK); const inset = Math.round(short * MARK_INSET);
        ctx.drawImage(mark, inset, h - size - inset, size, size);
      } catch (e) { /* no watermark beats no result */ }
      const blob = await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/jpeg', 0.92));
      return { blob, url: URL.createObjectURL(blob) };
    }

    /* ---------- Done: share and compare ---------- */

    shareFile() {
      return this.result && new File([this.result.blob], (this.download.dataset.filename || 'aura-on-my-car.jpg'), { type: 'image/jpeg' });
    }

    canShare() {
      try { return !!navigator.canShare && navigator.canShare({ files: [this.shareFile()] }); } catch (e) { return false; }
    }

    async share() {
      try {
        await navigator.share({ files: [this.shareFile()], text: this.strings.share_text, url: location.origin + location.pathname });
        track('aura_tryon_saved', { handle: this.dataset.handle, how: 'share' });
      } catch (e) { /* cancelled */ }
    }

    comparing(on) {
      this.root.classList.toggle('is-comparing', on);
      this.compareBtn.setAttribute('aria-pressed', String(on));
    }

    /* Hold (pointer, Space or Enter) to see the photo as it was */
    wireCompare() {
      const b = this.compareBtn;
      b.addEventListener('pointerdown', (e) => { b.setPointerCapture?.(e.pointerId); this.comparing(true); });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((t) => b.addEventListener(t, () => this.comparing(false)));
      b.addEventListener('keydown', (e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); this.comparing(true); } });
      b.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') this.comparing(false); });
      b.addEventListener('blur', () => this.comparing(false));
      b.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    /* ---------- Working: the paint loader ---------- */

    /* The film's colors from the explorer's catalog (about 1 KB, once): the base, then the shift colors its paint
       blends in, as Explorer4's loader lays them. Without it, aura.hex; without that, grey */
    loadPalette() {
      if (this.palettePromise) return this.palettePromise;
      const fallback = hexOf(this.dataset.hex) || '#808080';
      this.palette = [fallback];
      const want = this.dataset.handle;
      this.palettePromise = (async () => {
        const res = await fetch(`${this.dataset.catalog}?s=${encodeURIComponent(want.split('-').join(' '))}&v2=1`);
        if (!res.ok) return;
        const bytes = new Uint8Array(await res.arrayBuffer());
        const gz = bytes[0] === 0x1f && bytes[1] === 0x8b && window.DecompressionStream;
        const text = gz ? await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text() : new TextDecoder().decode(bytes);
        const records = (JSON.parse(text) || {}).array || [];
        const rec = records.find((r) => r && r.color && r.hash2 && perma(r.color) === want);
        if (rec) this.palette = paletteFor(JSON.parse(atob(rec.hash2)), fallback);
      })().catch(() => {});
      return this.palettePromise;
    }

    paint(on) {
      if (on === !!this.painting) return;
      this.painting = on;
      cancelAnimationFrame(this.raf);
      if (!on) {
        this.laid.style.opacity = 0;
        this.wipe.style.clipPath = 'inset(0 100% 0 0)';
        this.edge.style.opacity = 0;
        return;
      }
      this.barFill.style.clipPath = 'inset(0 100% 0 0)';
      this.laid.style.opacity = 0;
      const start = performance.now();
      let pass = -1;
      const frame = () => {
        if (!this.painting) return;
        const t = performance.now() - start;
        const still = reduced();
        /* Each pass lays the film's next color; the last one stays under the line */
        const k = Math.floor(t / PAINT_CYCLE);
        if (k !== pass) {
          pass = k;
          const list = this.palette;
          const next = list[k % list.length];
          const under = still ? next : k > 0 ? list[(k - 1) % list.length] : '';
          this.laid.style.background = under;
          this.laid.style.opacity = under ? 1 : 0;
          this.wipe.style.background = next;
          this.wipe.style.visibility = still ? 'hidden' : 'visible';
          this.drop.style.setProperty('--coat', next);
          this.drop.style.setProperty('--glow', accent(next));
          const glows = list.map(accent);
          if (glows.length === 1) glows.unshift('transparent');
          this.barFill.style.setProperty('--grad', `linear-gradient(90deg, ${glows.join(', ')})`);
        }
        const p = (t % PAINT_CYCLE) / PAINT_PASS;
        const W = this.fitBox.offsetWidth; const H = this.fitBox.offsetHeight;
        if (!still && p <= 1 && W && H) {
          const d = H / 2 * SLANT;
          const m = d * 1.2 + 24;
          const x = -m + easeInOut(p) * (W + 2 * m);
          this.wipe.style.clipPath = `polygon(0 0, ${x + d}px 0, ${x - d}px 100%, 0 100%)`;
          this.edge.style.transform = `translateX(${x}px) rotate(12deg)`;
          this.edge.style.opacity = 1;
        } else {
          this.wipe.style.clipPath = 'none';
          this.edge.style.opacity = 0;
        }
        const fill = Math.min(PAINT_MAX, 1 - Math.exp(-t / PAINT_EASE));
        this.barFill.style.clipPath = `inset(0 ${((1 - fill) * 100).toFixed(2)}% 0 0)`;
        this.raf = requestAnimationFrame(frame);
      };
      this.raf = requestAnimationFrame(frame);
    }
  }

  if (!customElements.get('aura-tryon')) customElements.define('aura-tryon', AuraTryon);
})();
